import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Building2, Plus, Search, X } from "lucide-react";
import { Button, FormField, Input, Label } from "@/design-system/components/ui";
import { Select, Textarea } from "@/components/ui/Select";
import { AddressFields } from "@/components/forms/AddressFields";
import { DedupCheckModal } from "@/components/counterparties/DedupCheckModal";
import { businessesApi } from "@/api/counterparties";
import { ApiError } from "@/api/client";
import { US_STATES } from "@/lib/usStates";
import { newDealBusinessKeys } from "./newDealKeys";
import {
  EMPTY_ADDRESS,
  type AddressDto,
  type CreateBusinessRequest,
  type EntityTypeCode,
} from "@/types/counterparties";

// New Deal wizard — subject Business lookup + inline create-new. Business is
// NOT party-first (it stays its own entity), so this searches businessesApi
// and reuses the shared DedupCheckModal ("offer existing") on create. The
// inline form collects exactly the Business form's required fields.

export interface SelectedBusiness {
  id: string;
  name: string;
  detail?: string | null;
}

interface Props {
  value: SelectedBusiness | null;
  onChange: (value: SelectedBusiness | null) => void;
  label?: string;
}

const ENTITY_TYPES: EntityTypeCode[] = [
  "LLC",
  "SCorp",
  "CCorp",
  "LP",
  "LLP",
  "Trust",
  "SoleProp",
  "Other",
];

function useDebounced(value: string, ms = 250): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export function DealBusinessLookup({ value, onChange, label = "Business" }: Props) {
  const [creating, setCreating] = useState(false);

  if (creating) {
    return (
      <InlineBusinessCreate
        label={label}
        onCreated={(b) => {
          onChange(b);
          setCreating(false);
        }}
        onCancel={() => setCreating(false)}
      />
    );
  }

  if (value) {
    return (
      <div>
        <Label>{label}</Label>
        <span
          className="inline-flex items-center gap-space-2 rounded-full border border-border-light bg-white-warm px-space-2 py-space-1 text-small text-onyx"
          data-testid="business-chip"
        >
          <Building2 size={12} aria-hidden />
          <span className="font-semibold">{value.name}</span>
          {value.detail && <span className="text-caption text-gray-mid">{value.detail}</span>}
          <button
            type="button"
            onClick={() => onChange(null)}
            aria-label="Clear selected business"
            className="text-gray-mid hover:text-onyx"
            data-testid="business-chip-clear"
          >
            <X size={12} aria-hidden />
          </button>
        </span>
      </div>
    );
  }

  return <BusinessSearch label={label} onSelect={onChange} onCreateNew={() => setCreating(true)} />;
}

function BusinessSearch({
  label,
  onSelect,
  onCreateNew,
}: {
  label: string;
  onSelect: (b: SelectedBusiness) => void;
  onCreateNew: () => void;
}) {
  const [search, setSearch] = useState("");
  const debounced = useDebounced(search);
  const term = debounced.trim();
  const enabled = term.length >= 2;

  const results = useQuery({
    queryKey: newDealBusinessKeys.picker(term),
    queryFn: () => businessesApi.list({ q: term, pageSize: 20 }),
    enabled,
  });
  const rows = useMemo(() => results.data?.data ?? [], [results.data]);

  return (
    <div className="flex flex-col gap-space-1">
      <Label htmlFor="business-search">{label}</Label>
      <div className="relative">
        <Search
          size={14}
          className="absolute left-space-2 top-1/2 -translate-y-1/2 text-gray-mid"
          aria-hidden
        />
        <Input
          id="business-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search businesses by name…"
          className="pl-[28px]"
          autoComplete="off"
          data-testid="business-search-input"
        />
      </div>
      {search.trim().length > 0 && search.trim().length < 2 && (
        <p className="text-caption text-gray-mid m-0">Type at least 2 characters to search.</p>
      )}
      {enabled && (
        <ul
          className="m-0 mt-space-1 p-0 list-none max-h-56 overflow-y-auto rounded-md border border-border-light bg-white-pure"
          data-testid="business-search-results"
        >
          {results.isLoading && (
            <li className="px-space-2 py-space-1 text-small text-gray-mid italic">Searching…</li>
          )}
          {!results.isLoading && rows.length === 0 && (
            <li className="px-space-2 py-space-1 text-small text-gray-mid">No businesses match.</li>
          )}
          {rows.map((b) => (
            <li key={b.id} role="presentation">
              <button
                type="button"
                onClick={() =>
                  onSelect({
                    id: b.id,
                    name: b.legalEntityName,
                    detail: b.naicsCode ?? b.hqState ?? null,
                  })
                }
                className="w-full flex items-baseline gap-space-2 px-space-2 py-space-1 text-left hover:bg-white-warm"
                data-testid={`business-option-${b.id}`}
              >
                <span className="text-small text-onyx font-semibold truncate">
                  {b.legalEntityName}
                </span>
                {b.naicsCode && (
                  <span className="text-caption text-gray-mid truncate">{b.naicsCode}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        onClick={onCreateNew}
        className="self-start inline-flex items-center gap-space-1 text-caption uppercase tracking-wide text-gold-shadow hover:underline"
        data-testid="create-business"
      >
        <Plus size={12} aria-hidden /> Create new business
      </button>
    </div>
  );
}

function InlineBusinessCreate({
  label,
  onCreated,
  onCancel,
}: {
  label: string;
  onCreated: (b: SelectedBusiness) => void;
  onCancel: () => void;
}) {
  const [legalEntityName, setLegalEntityName] = useState("");
  const [entityType, setEntityType] = useState<EntityTypeCode>("LLC");
  const [stateOfFormation, setStateOfFormation] = useState("");
  const [naicsCode, setNaicsCode] = useState("");
  const [industryDescription, setIndustryDescription] = useState("");
  const [hqAddress, setHqAddress] = useState<AddressDto>({ ...EMPTY_ADDRESS });
  const [error, setError] = useState<string | null>(null);
  const [showDedup, setShowDedup] = useState(false);

  const requiredComplete =
    !!legalEntityName.trim() &&
    !!stateOfFormation.trim() &&
    !!naicsCode.trim() &&
    !!industryDescription.trim() &&
    !!hqAddress.streetAddress1.trim() &&
    !!hqAddress.city.trim() &&
    !!hqAddress.state.trim() &&
    !!hqAddress.zip.trim();

  const buildBody = (confirmCreateNew: boolean, reason?: string): CreateBusinessRequest => ({
    legalEntityName: legalEntityName.trim(),
    entityType,
    stateOfFormation: stateOfFormation.trim(),
    naicsCode: naicsCode.trim(),
    industryDescription: industryDescription.trim(),
    hqAddress,
    confirmCreateNew,
    createNewReason: reason || null,
  });

  const queryClient = useQueryClient();
  const create = useMutation({
    mutationFn: (reason: string) => businessesApi.create(buildBody(true, reason)),
    onSuccess: (b) => {
      void queryClient.invalidateQueries({ queryKey: newDealBusinessKeys.all });
      onCreated({ id: b.id, name: b.legalEntityName, detail: b.naicsCode });
    },
    onError: (err) =>
      setError(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : "Could not create business.",
      ),
  });

  return (
    <div className="flex flex-col gap-space-2 rounded-md border border-border-light bg-white-warm p-space-3">
      <div className="flex items-center justify-between">
        <Label>New {label.toLowerCase()}</Label>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-space-1 text-caption uppercase tracking-wide text-gray-mid hover:text-onyx"
        >
          <ArrowLeft size={12} aria-hidden /> Back to search
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-space-2">
        <FormField label="Legal entity name" required htmlFor="biz-legal">
          <Input
            id="biz-legal"
            value={legalEntityName}
            onChange={(e) => setLegalEntityName(e.target.value)}
          />
        </FormField>
        <FormField label="Entity type" required htmlFor="biz-type">
          <Select
            id="biz-type"
            value={entityType}
            onChange={(e) => setEntityType(e.target.value as EntityTypeCode)}
          >
            {ENTITY_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="State of formation" required htmlFor="biz-state">
          <Select
            id="biz-state"
            value={stateOfFormation}
            onChange={(e) => setStateOfFormation(e.target.value)}
          >
            <option value="">— Select state —</option>
            {US_STATES.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="NAICS code" required htmlFor="biz-naics">
          <Input
            id="biz-naics"
            value={naicsCode}
            onChange={(e) => setNaicsCode(e.target.value)}
            placeholder="6-digit NAICS"
            maxLength={6}
          />
        </FormField>
        <FormField
          label="Industry description"
          required
          className="md:col-span-2"
          htmlFor="biz-industry"
        >
          <Textarea
            id="biz-industry"
            rows={2}
            value={industryDescription}
            onChange={(e) => setIndustryDescription(e.target.value)}
          />
        </FormField>
      </div>

      <div>
        <Label>HQ address</Label>
        <AddressFields
          value={hqAddress}
          onChange={setHqAddress}
          required
          idPrefix="biz-hq"
          labels={{ streetAddress1: "HQ street address", city: "HQ city", state: "HQ state" }}
        />
      </div>

      {error && (
        <p className="text-small text-status-error m-0" role="alert">
          {error}
        </p>
      )}

      <div className="flex justify-end">
        <Button
          onClick={() => {
            setError(null);
            setShowDedup(true);
          }}
          disabled={!requiredComplete}
          loading={create.isPending}
        >
          Create business
        </Button>
      </div>

      <DedupCheckModal
        open={showDedup}
        kind="Business"
        request={{ legalEntityName: legalEntityName.trim() }}
        onLinkExisting={(candidate) => {
          setShowDedup(false);
          onCreated({ id: candidate.id, name: candidate.displayName });
        }}
        onCreateNew={(reason) => {
          setShowDedup(false);
          create.mutate(reason);
        }}
        onCancel={() => setShowDedup(false)}
      />
    </div>
  );
}
